import React, { memo, useCallback, useMemo, useState } from 'react';
import type { Chat } from '@trinity/chat';
import { ChatMarkdown } from '@trinity/chat-components/components/Markdown/Markdown';
import { AttachmentsContext } from '../Chat/context';
import { ChatMessageAttachments } from '../Chat/Message.Attachments';
import { EntryButtons } from './DynamicForm.introduction.buttons';
import { DraftTable, type DraftPreview } from './DynamicForm.introduction.drafts';
import {
  Message,
  VoiceWarningBox,
} from './DynamicForm.styles.index';

// Production constants
const CONSTANTS = {
  DEFAULT_PROMPT: '',
  DEFAULT_WARNING: '',
} as const;

interface DynamicFormIntroductionProps {
  onManualEntry: () => void;
  onSpeechEntry: () => void;
  onCancel: () => void;
  onLoadDraft?: (draftId: string) => void;
  prompt?: string;
  warningMessage?: string;
  showCancelButton?: boolean;
  showSpeechOption?: boolean;
  showManualButton?: boolean;
  showWarning?: boolean;
  isVoiceActive?: boolean;
  availableDrafts?: DraftPreview[];
  onViewDrafts?: () => void;
  draftOnlyMode?: boolean; // When true, only show draft options, hide new entry buttons
  introResponse?: { value: string; attachments?: unknown[] }; // Optional response content to display above form entry options
}

export const DynamicFormIntroduction: React.FC<DynamicFormIntroductionProps> = memo(({
  onManualEntry,
  onSpeechEntry,
  onCancel,
  onLoadDraft,
  prompt = CONSTANTS.DEFAULT_PROMPT,
  warningMessage,
  showCancelButton = true,
  showSpeechOption = true,
  showManualButton = true,
  showWarning = true,
  isVoiceActive = false,
  availableDrafts = [],
  onViewDrafts,
  draftOnlyMode = false,
  introResponse,
}) => {
  const [showVoiceWarning, setShowVoiceWarning] = useState(false);

  const handleManualEntry = useCallback(() => {
    try {
      setShowVoiceWarning(false);
      if (onManualEntry && typeof onManualEntry === 'function') {
        onManualEntry();
      }
    } catch {
      // no-op in UI
    }
  }, [onManualEntry]);

  const handleSpeechEntry = useCallback(() => {
    try {
      // Show warning box immediately when mic is clicked
      setShowVoiceWarning(true);
      if (onSpeechEntry && typeof onSpeechEntry === 'function') {
        onSpeechEntry();
      }
    } catch {
      // no-op in UI
    }
  }, [onSpeechEntry]);

  const handleCancel = useCallback(() => {
    try {
      setShowVoiceWarning(false);
      if (onCancel && typeof onCancel === 'function') {
        onCancel();
      }
    } catch {
      // no-op in UI
    }
  }, [onCancel]);

  // Memoized computed values for better performance
  const computedValues = useMemo(() => {
    // Validate and sanitize prompt
    const validPrompt = typeof prompt === 'string' && prompt.trim()
      ? prompt.trim()
      : CONSTANTS.DEFAULT_PROMPT;

    // Validate and sanitize warning message
    const validWarning = typeof warningMessage === 'string' && warningMessage.trim()
      ? warningMessage.trim()
      : CONSTANTS.DEFAULT_WARNING;

    return {
      validPrompt,
      validWarning,
    };
  }, [prompt, warningMessage]);

  const shouldShowButtons = !showVoiceWarning && !isVoiceActive && !draftOnlyMode;

  // Normalize attachments for context (default to empty array)
  const introAttachments = useMemo(() => {
    return (introResponse?.attachments as Chat.Attachment.Item[] | undefined) || [];
  }, [introResponse?.attachments]);

  return (
    <>
      {introResponse && introResponse.value && introResponse.value.trim() && (
        <AttachmentsContext.Provider value={introAttachments}>
          <ChatMarkdown value={introResponse.value} />
          {introResponse.attachments && Array.isArray(introResponse.attachments) && introResponse.attachments.length > 0 && (
            <ChatMessageAttachments attachments={introResponse.attachments as Chat.Attachment.Item[]} />
          )}
        </AttachmentsContext.Provider>
      )}

      {computedValues.validPrompt && (
        <Message role="status" aria-live="polite">
          {computedValues.validPrompt}
        </Message>
      )}

      {draftOnlyMode && availableDrafts && availableDrafts.length > 0 && (
        <DraftTable
          availableDrafts={availableDrafts}
          onLoadDraft={onLoadDraft || (() => { })}
        />
      )}

      {shouldShowButtons && (
        <EntryButtons
          onManualEntry={handleManualEntry}
          onSpeechEntry={handleSpeechEntry}
          onCancel={handleCancel}
          showManualButton={showManualButton}
          showSpeechOption={showSpeechOption}
          showCancelButton={showCancelButton}
        />
      )}

      {(showVoiceWarning || isVoiceActive) && warningMessage && warningMessage.trim().length > 0 && (
        <VoiceWarningBox
          role="region"
          aria-label="Voice input instructions"
        >
          {warningMessage}
        </VoiceWarningBox>
      )}
    </>
  );
});

DynamicFormIntroduction.displayName = 'DynamicFormIntroduction';

export default DynamicFormIntroduction;
export type { DraftPreview } from './DynamicForm.introduction.drafts';